import { ReactElement } from 'react';
import { LinearFit } from './auxSensorChannels';
import { AUX_ACCENT } from './auxPlotStyle';
import RangeSlider from 'components/RangeSlider';

export interface AuxFitReadout {
  /** Series the fit belongs to — shown only when more than one is fitted. */
  name?: string;
  color?: string;
  fit: LinearFit | null;
}

/** How the slope and offset numbers are rendered for a given plot's units. */
export interface AuxFitFormat {
  /** Unit appended to the intercept (same units as the y-axis). */
  offsetUnit?: string;
  /** Unit appended to the slope, e.g. `/N` for a per-newton gradient. */
  slopeUnit?: string;
  slopeDigits?: number;
  offsetDigits?: number;
}

/** Held-intercept option: the offset is user-supplied and only the slope is fitted. */
export interface AuxManualOffset {
  enabled: boolean;
  value: number;
  onToggle: (enabled: boolean) => void;
  onChange: (value: number) => void;
  step?: number;
}

interface AuxFitControlsProps {
  showFit: boolean;
  onToggleFit: (value: boolean) => void;
  /** X-axis fit window; points outside it are greyed and left unfitted. */
  window: { low: number; high: number };
  onWindowChange: (window: { low: number; high: number }) => void;
  sliderLabel: string;
  sliderUnit: string;
  sliderMax: number;
  sliderStep?: number;
  readouts: AuxFitReadout[];
  manualOffset?: AuxManualOffset;
  format?: AuxFitFormat;
}

const CHECKBOX_STYLE = { accentColor: AUX_ACCENT, width: '16px', height: '16px' };

function FitReadout({
  readout: { fit, name, color },
  showName,
  format,
  offsetHeld,
}: {
  readout: AuxFitReadout;
  showName: boolean;
  format: AuxFitFormat;
  offsetHeld: boolean;
}): ReactElement {
  const { slopeDigits = 4, offsetDigits = 2, slopeUnit = '', offsetUnit = '' } = format;
  if (!fit) {
    return (
      <span className="cr-text-gray-400">
        {showName && name ? `${name}: ` : ''}
        Not enough points in the fit window.
      </span>
    );
  }
  return (
    <span className="cr-tabular-nums cr-text-gray-700">
      {showName && name ? (
        <span className="cr-font-medium" style={color ? { color } : undefined}>{`${name}: `}</span>
      ) : null}
      <span className="cr-font-medium">Slope</span> {fit.slope.toFixed(slopeDigits)}
      {slopeUnit}
      <span className="cr-px-2 cr-text-gray-300">|</span>
      <span className="cr-font-medium">Offset</span> {fit.intercept.toFixed(offsetDigits)}
      {offsetUnit ? ` ${offsetUnit}` : ''}
      {offsetHeld ? <span className="cr-pl-1 cr-text-gray-400">(held)</span> : null}
      <span className="cr-px-2 cr-text-gray-300">|</span>
      <span className="cr-font-medium">R²</span> {fit.r2.toFixed(4)}
      <span className="cr-pl-2 cr-text-gray-400">{`(n = ${fit.count})`}</span>
    </span>
  );
}

/**
 * "Show fit" toggle for the aux-sensor scatter plots. When enabled it reveals the
 * x-axis fit window on its own full-width row — beside the checkbox there is too
 * little travel to place two thumbs — and prints the slope, offset and R² of each
 * fit. With `manualOffset`, a second checkbox pins the offset to a typed value
 * (defaulting to 0) and fits only the slope through it.
 */
export default function AuxFitControls({
  showFit,
  onToggleFit,
  window,
  onWindowChange,
  sliderLabel,
  sliderUnit,
  sliderMax,
  sliderStep = 1,
  readouts,
  manualOffset,
  format = {},
}: AuxFitControlsProps): ReactElement {
  const showName = readouts.length > 1;
  const { low, high } = window;

  return (
    <div className="cr-mb-3 cr-space-y-2 cr-text-sm">
      <label className="cr-flex cr-w-fit cr-cursor-pointer cr-items-center cr-gap-2">
        <input
          type="checkbox"
          checked={showFit}
          onChange={(e) => onToggleFit(e.target.checked)}
          className="cr-cursor-pointer"
          style={CHECKBOX_STYLE}
        />
        <span className="cr-font-medium cr-text-gray-600">Show fit</span>
      </label>
      {showFit ? (
        <div className="cr-flex cr-w-full cr-items-center cr-gap-3">
          <span className="cr-whitespace-nowrap cr-font-medium cr-text-gray-600">{sliderLabel}</span>
          <RangeSlider
            min={0}
            max={sliderMax || 1}
            step={sliderStep}
            low={low}
            high={high}
            onChange={(nextLow, nextHigh) => onWindowChange({ low: nextLow, high: nextHigh })}
            ariaLabelLow={`${sliderLabel} minimum`}
            ariaLabelHigh={`${sliderLabel} maximum`}
          />
          <span className="cr-w-32 cr-whitespace-nowrap cr-tabular-nums cr-text-gray-700">
            {low.toFixed(0)} – {high.toFixed(0)} {sliderUnit}
          </span>
        </div>
      ) : null}
      {showFit && manualOffset ? (
        <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-x-3 cr-gap-y-2">
          <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2">
            <input
              type="checkbox"
              checked={manualOffset.enabled}
              onChange={(e) => manualOffset.onToggle(e.target.checked)}
              className="cr-cursor-pointer"
              style={CHECKBOX_STYLE}
            />
            <span className="cr-font-medium cr-text-gray-600">Manual offset</span>
          </label>
          <input
            type="number"
            value={manualOffset.value}
            step={manualOffset.step ?? 1}
            disabled={!manualOffset.enabled}
            onChange={(e) => {
              const parsed = parseFloat(e.target.value);
              manualOffset.onChange(Number.isNaN(parsed) ? 0 : parsed);
            }}
            aria-label="Manual offset value"
            className="cr-w-24 cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-1
              cr-text-right cr-tabular-nums disabled:cr-bg-gray-100 disabled:cr-text-gray-400"
          />
          <span className="cr-text-gray-500">
            {format.offsetUnit}
            <span className="cr-pl-2 cr-text-gray-400">— offset held, slope fitted through it</span>
          </span>
        </div>
      ) : null}
      {showFit ? (
        <div className="cr-space-y-1">
          {readouts.map((r, i) => (
            <div key={r.name ?? i}>
              <FitReadout
                readout={r}
                showName={showName}
                format={format}
                offsetHeld={!!manualOffset?.enabled}
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
